import { createSelector } from 'reselect'
import {
  getFinancialAccountData,
} from 'utils/financialAccountTypes'

import {
  formatEntity,
  formatEntities,
} from 'utils/formatter'

import sectionsList from 'utils/sectionsList'

const getFinancialAccountSectionCompleteStatus = (financialAccount, financialAccountType) => {
  const sectionsStatus = {}
  const hasJointOwner = !!financialAccount.jointOwner
  const sections = sectionsList[financialAccountType]
  const sectionNames = Object.keys(sections)

  sectionNames.forEach((sectionToReview) => {
    const requiredSections = sections[sectionToReview].completeStatusFields

    sectionsStatus[sectionToReview] = {
      complete: requiredSections.every(section => financialAccount[section]),
    }
  })

  if (!hasJointOwner && sectionsStatus.jointOwner) {
    delete sectionsStatus.jointOwner
  }

  return {
    complete: Object.values(sectionsStatus).every(section => section.complete),
    sectionsStatus,
  }
}

const getFinancialAccountCompleteStatus = (financialAccount) => {
  const { financialAccountType } = financialAccount
  const financialAccountData = getFinancialAccountData(financialAccountType, 'RAW_TYPE')

  return getFinancialAccountSectionCompleteStatus(financialAccount, financialAccountData.SECTION)
}

/* Selectors */

const getLocationState = state => state.location
const getProspectState = state => state.prospect
const getFinancialAccountsState = state => state.financialAccounts
const getJointOwnersState = state => state.jointOwners

const getProspect = createSelector(
  getProspectState,
  state => state.prospect,
)

const getFinancialAccounts = createSelector(
  getFinancialAccountsState,
  state => state.financialAccounts,
)

const getJointOwners = createSelector(
  getJointOwnersState,
  state => state.jointOwners,
)

export const getCurrentFinancialAccountId = createSelector(
  getLocationState,
  state => state.payload.financialAccountId
)

export const getCurrentContactId = createSelector(
  getLocationState,
  state => state.payload.contactId
)

const getCurrentFinancialAccount = createSelector(
  getCurrentFinancialAccountId,
  getFinancialAccounts,
  (financialAccountId, financialAccounts) =>
    financialAccounts.find(financialAccount => financialAccount.id === financialAccountId)
)

const getCurrentJointOwner = createSelector(
  getCurrentFinancialAccountId,
  getJointOwners,
  (financialAccountId, jointOwners) =>
    jointOwners.find(jointOwner => jointOwner.financialAccountId === financialAccountId)
)

const getAboutYouOverview = createSelector(
  getProspect,
  prospect => ({
    personalInformation:  { complete: prospect.reviewedPersonalInformation },
    identityVerification: { complete: prospect.reviewedIdentityVerification },
    employmentDetails: { complete: prospect.reviewedEmploymentDetails },
  })
)

const getYourAccountsOverview = createSelector(
  getFinancialAccounts,
  (financialAccounts) => {
    const formattedFinancialAccounts = formatEntities(financialAccounts)
    return formattedFinancialAccounts.map((financialAccount) => {
      const { id } = financialAccount
      const financialAccountStatus = getFinancialAccountCompleteStatus(financialAccount)

      return {
        financialAccountId: id,
        complete: financialAccountStatus.complete,
        sections: financialAccountStatus.sectionsStatus,
      }
    })
  }
)

export const getOverview = createSelector(
  getAboutYouOverview,
  getYourAccountsOverview,
  (onboardingOverview, financialAccountsOverview) => ({
    aboutYou: {
      ...onboardingOverview,
    },
    yourAccounts: [...financialAccountsOverview],
  })
)

export const getFinancialAccountRelatedData = createSelector(
  getCurrentFinancialAccount,
  getCurrentJointOwner,
  (financialAccount, jointOwner) => {
    const formattedFinancialAccount = formatEntity(financialAccount)
    const formattedJointOwner = formatEntity(jointOwner && jointOwner.data)
    return ({
      financialAccount: formattedFinancialAccount,
      jointOwner: formattedJointOwner,
    })
  }
)

export default {
  getFinancialAccountRelatedData,
  getOverview,
}



// WEBPACK FOOTER //
// ./src/selectors.js